import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Form,
  Input,
  Select,
  Button,
  Alert,
  Typography,
  Space,
  Tag,
  Divider,
  Row,
  Col,
  Badge,
  Tooltip,
  DatePicker,
} from "antd";
import {
  EnvironmentOutlined,
  CalendarOutlined,
  InboxOutlined,
  FileTextOutlined,
  PlusOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { logDebug } from "../shared/utils/logger.js";
import { GoogleAddressAutocompleteAntd } from "./AddressInput/GoogleAddressAutocompleteAntd.jsx";

const { Text } = Typography;
const { TextArea } = Input;

/**
 * EditLeadForm - Enhanced mobile-first form for editing lead data
 * Features:
 * - Mobile-first design with progressive enhancement
 * - Touch-friendly interface
 * - Responsive layout for all screen sizes
 * - Enhanced UX with better visual feedback
 * - Accessibility improvements
 */
const EditLeadForm = ({ initialData, onSubmit, loading, error }) => {
  logDebug("EditLeadForm: initial data", { initialData });
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [pickupAddressData, setPickupAddressData] = useState(
    initialData?.pickupAddress || null
  );
  const [deliveryAddressData, setDeliveryAddressData] = useState(
    initialData?.deliveryAddress || null
  );

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Initialize form with data
  useEffect(() => {
    if (initialData) {
      // Support both legacy move_date and new move_dates array
      const datesArray =
        initialData.move_dates && initialData.move_dates.length > 0
          ? initialData.move_dates
          : initialData.move_date
            ? [initialData.move_date]
            : [null]; // Start with one empty field

      // Set form values
      form.setFieldsValue({
        from: initialData.moving_from || "",
        to: initialData.moving_to || "",
        date: initialData.move_date || "Not sure", // Keep for backward compatibility
        dates: datesArray.map((d) => {
          // Convert date strings to dayjs objects, or null for empty fields
          if (!d || d === "Not sure" || d === "Urgently" || d === "This week" || d === "This month" || d === "Next month" || d === "In 2-3 months") {
            return null; // Use null for empty or preset values, DatePicker will handle it
          }
          const parsed = dayjs(d);
          return parsed.isValid() ? parsed : null;
        }),
        notes: initialData.notes || "",
        serviceType: initialData.qualification?.serviceType || "",
      });

      // Set items - extract content from item objects
      const itemsContent = (initialData.items || [])
        .map((item) => (typeof item === "string" ? item : item.content || ""))
        .filter((content) => content.trim() !== "");
      setItems(itemsContent);
      setPickupAddressData(initialData.pickupAddress || null);
      setDeliveryAddressData(initialData.deliveryAddress || null);
    }
  }, [initialData, form]);

  // Track form changes
  const handleFormChange = () => {
    setHasChanges(true);
  };

  // Handle adding new item
  const handleAddItem = () => {
    if (newItem.trim() && !items.includes(newItem.trim())) {
      const updatedItems = [...items, newItem.trim()];
      setItems(updatedItems);
      setNewItem("");
      setHasChanges(true);
    }
  };

  // Handle removing item
  const handleRemoveItem = (itemToRemove) => {
    const updatedItems = items.filter((item) => item !== itemToRemove);
    setItems(updatedItems);
    setHasChanges(true);
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      // Validate items
      if (items.length === 0) {
        form.setFields([
          {
            name: "items",
            errors: ["Please add at least one item to move"],
          },
        ]);
        return;
      }

      // Normalize dates: use dates array if provided, otherwise convert date to array
      const datesArray = values.dates && values.dates.length > 0
        ? values.dates.map((d) => {
            // Convert dayjs objects to YYYY-MM-DD strings, or keep preset values as-is
            if (typeof d === "string") return d;
            return dayjs.isDayjs(d) ? d.format("YYYY-MM-DD") : String(d);
          }).filter((d) => d && d !== "Not sure")
        : (values.date && values.date !== "Not sure" ? [values.date] : []);

      // Ensure at least one date is provided
      if (datesArray.length === 0) {
        form.setFields([
          {
            name: "dates",
            errors: ["Please provide at least one move date"],
          },
        ]);
        return;
      }

      // Prepare form data
      const formData = {
        from: values.from.trim(),
        to: values.to.trim(),
        date: datesArray[0] || values.date || "Not sure", // Keep for backward compatibility
        dates: datesArray, // New: array of dates
        items: items,
        notes: values.notes?.trim() || "",
        pickupAddress: pickupAddressData || null,
        deliveryAddress: deliveryAddressData || null,
        qualification: {
          serviceType:
            values.serviceType === "shared_load" ||
            values.serviceType === "exclusive_load"
              ? values.serviceType
              : null,
        },
      };

      await onSubmit(formData);
    } catch (err) {
      console.error("Form submission error:", err);
    }
  };

  // Handle Enter key for adding items
  const handleItemKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddItem();
    }
  };

  // Mobile section navigation
  const sections = [
    { key: "locations", title: "Locations", icon: <EnvironmentOutlined /> },
    { key: "date", title: "Date", icon: <CalendarOutlined /> },
    { key: "items", title: "Items", icon: <InboxOutlined /> },
    { key: "notes", title: "Notes", icon: <FileTextOutlined /> },
  ];

  const renderMobileNavigation = () => {
    if (!isMobile) return null;

    return (
      <MobileNav>
        {sections.map((section) => (
          <NavItem
            key={section.key}
            active={activeSection === section.key}
            onClick={() => setActiveSection(section.key)}
          >
            {section.icon}
            <span>{section.title}</span>
          </NavItem>
        ))}
      </MobileNav>
    );
  };

  const renderFormSection = (key, title, icon, children) => {
    const isActive =
      !isMobile || activeSection === key || activeSection === null;

    return (
      <FormSection key={key} $isActive={isActive} $isMobile={isMobile}>
        <SectionHeader>
          <SectionTitle>
            {icon}
            {title}
          </SectionTitle>
          {isMobile && (
            <Tooltip title="Tap to expand this section">
              <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
            </Tooltip>
          )}
        </SectionHeader>
        {isActive && children}
      </FormSection>
    );
  };

  return (
    <FormContainer $isMobile={isMobile}>
      {/* Device indicator */}
      <DeviceIndicator>
        {isMobile ? (
          <Badge status="processing" text="Mobile View" />
        ) : (
          <Badge status="success" text="Desktop View" />
        )}
      </DeviceIndicator>

      {error && (
        <Alert
          message="Update Failed"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Mobile Navigation */}
      {renderMobileNavigation()}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleFormChange}
        size={isMobile ? "middle" : "large"}
      >
        {/* Locations Section */}
        {renderFormSection(
          "locations",
          "Pickup & Delivery Locations",
          <EnvironmentOutlined style={{ marginRight: 8, color: "#1890ff" }} />,
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                name="from"
                label="Pickup Location"
                rules={[
                  { required: true, message: "Please enter pickup location" },
                  {
                    max: 200,
                    message: "Location must be less than 200 characters",
                  },
                ]}
              >
                <GoogleAddressAutocompleteAntd
                  placeholder="Where are we picking up from?"
                  style={{ borderRadius: "8px" }}
                  prefix={<EnvironmentOutlined style={{ color: "#1890ff" }} />}
                  onPlaceSelect={(addressData) => {
                    form.setFieldsValue({ from: addressData.address });
                    setPickupAddressData(addressData);
                    setHasChanges(true);
                    logDebug("Pickup address selected", addressData);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                name="to"
                label="Delivery Location"
                rules={[
                  { required: true, message: "Please enter delivery location" },
                  {
                    max: 200,
                    message: "Location must be less than 200 characters",
                  },
                ]}
              >
                <GoogleAddressAutocompleteAntd
                  placeholder="Where are we delivering to?"
                  style={{ borderRadius: "8px" }}
                  prefix={<EnvironmentOutlined style={{ color: "#52c41a" }} />}
                  onPlaceSelect={(addressData) => {
                    form.setFieldsValue({ to: addressData.address });
                    setDeliveryAddressData(addressData);
                    setHasChanges(true);
                    logDebug("Delivery address selected", addressData);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Date Section */}
        {renderFormSection(
          "date",
          "Moving Dates",
          <CalendarOutlined style={{ marginRight: 8, color: "#fa8c16" }} />,
          <Form.Item
            label="Move Dates"
            required
            style={{ marginBottom: 16 }}
          >
            <Form.List name="dates">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space
                      key={key}
                      style={{ display: "flex", marginBottom: 8 }}
                      align="baseline"
                    >
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[
                          { required: true, message: "Date is required" },
                        ]}
                        style={{ marginBottom: 0, flex: 1 }}
                      >
                        <DatePicker
                          style={{ width: "100%", borderRadius: "8px" }}
                          placeholder="Select move date"
                          format="YYYY-MM-DD"
                          size={isMobile ? "middle" : "large"}
                        />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                          size={isMobile ? "middle" : "large"}
                        />
                      )}
                    </Space>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      size={isMobile ? "middle" : "large"}
                      style={{ borderRadius: "8px" }}
                    >
                      Add another date
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
        )}

        <Form.Item
          label="Service type"
          name="serviceType"
          extra="Shared load uses corridor rates. Exclusive load is a dedicated vehicle."
        >
          <Select
            placeholder="Select service type"
            size={isMobile ? "middle" : "large"}
            allowClear
            options={[
              { value: "shared_load", label: "Shared load" },
              { value: "exclusive_load", label: "Exclusive load" },
            ]}
          />
        </Form.Item>

        {/* Items Section */}
        {renderFormSection(
          "items",
          "Items to Move",
          <InboxOutlined style={{ marginRight: 8, color: "#722ed1" }} />,
          <>
            {/* Current Items */}
            <ItemsContainer $isMobile={isMobile}>
              {items.length > 0 ? (
                <Space wrap>
                  {items.map((item, index) => (
                    <Tag
                      key={index}
                      closable
                      onClose={() => handleRemoveItem(item)}
                      style={{
                        marginBottom: 8,
                        padding: "6px 12px",
                        fontSize: isMobile ? "13px" : "14px",
                        borderRadius: "16px",
                        cursor: "pointer",
                      }}
                    >
                      {item}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <EmptyState>
                  <InboxOutlined
                    style={{ fontSize: "24px", color: "#d9d9d9" }}
                  />
                  <Text type="secondary" style={{ fontStyle: "italic" }}>
                    No items added yet
                  </Text>
                </EmptyState>
              )}
            </ItemsContainer>

            {/* Add New Item */}
            <AddItemContainer>
              <Input
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyPress={handleItemKeyPress}
                placeholder="Add an item (e.g., Sofa, Refrigerator, Boxes)"
                style={{ borderRadius: "8px" }}
                size={isMobile ? "middle" : "large"}
                suffix={
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={handleAddItem}
                    disabled={!newItem.trim()}
                    style={{ color: "#1890ff" }}
                  />
                }
              />
            </AddItemContainer>

            {/* Items validation error */}
            <Form.Item name="items" style={{ marginBottom: 0 }}>
              <Input style={{ display: "none" }} />
            </Form.Item>
          </>
        )}

        {/* Notes Section */}
        {renderFormSection(
          "notes",
          "Special Instructions",
          <FileTextOutlined style={{ marginRight: 8, color: "#13c2c2" }} />,
          <Form.Item
            name="notes"
            rules={[
              { max: 1000, message: "Notes must be less than 1000 characters" },
            ]}
          >
            <TextArea
              rows={isMobile ? 3 : 4}
              placeholder="Any special instructions, fragile items, or additional details..."
              style={{ borderRadius: "8px" }}
            />
          </Form.Item>
        )}

        <Divider />

        {/* Submit Button */}
        <SubmitContainer $isMobile={isMobile}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={!hasChanges && !loading}
            icon={<SaveOutlined />}
            size={isMobile ? "middle" : "large"}
            style={{
              width: "100%",
              height: isMobile ? "44px" : "48px",
              borderRadius: "8px",
              fontSize: isMobile ? "15px" : "16px",
              fontWeight: "bold",
            }}
          >
            {loading ? "Saving Changes..." : "Save Changes"}
          </Button>

          {!hasChanges && !loading && (
            <Text
              type="secondary"
              style={{
                textAlign: "center",
                marginTop: 8,
                fontSize: isMobile ? "13px" : "14px",
              }}
            >
              Make some changes to enable saving
            </Text>
          )}
        </SubmitContainer>
      </Form>
    </FormContainer>
  );
};

// Styled components
const FormContainer = styled.div`
  padding: ${(props) => (props.$isMobile ? "16px" : "24px")};
  max-width: 800px;
  margin: 0 auto;
`;

const DeviceIndicator = styled.div`
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileNav = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 8px;

  @media (min-width: 768px) {
    display: none;
  }
`;

const NavItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props) => (props.active ? "#1890ff" : "transparent")};
  color: ${(props) => (props.active ? "white" : "#666")};

  span {
    font-size: 12px;
    margin-top: 4px;
  }

  &:hover {
    background: ${(props) => (props.active ? "#1890ff" : "#e6f7ff")};
  }
`;

const FormSection = styled.div`
  margin-bottom: ${(props) => (props.$isMobile ? "24px" : "32px")};
  display: ${(props) =>
    props.$isMobile && !props.$isActive ? "none" : "block"};

  @media (min-width: 768px) {
    display: block;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: ${(props) => (props.$isMobile ? "15px" : "16px")};
  font-weight: 600;
  color: #262626;
`;

const ItemsContainer = styled.div`
  min-height: ${(props) => (props.$isMobile ? "50px" : "60px")};
  background: #fafafa;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: ${(props) => (props.$isMobile ? "8px" : "12px")};
  margin-bottom: 12px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #8c8c8c;
`;

const AddItemContainer = styled.div`
  margin-bottom: 8px;
`;

const SubmitContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${(props) => (props.$isMobile ? "24px" : "32px")};
`;

export default EditLeadForm;
